import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const Container = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  margin-bottom: 2rem;
  color: white;
  font-size: 2rem;
  font-weight: 700;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-align: center;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const FullWidthField = styled.div`
  grid-column: 1 / -1;
`;

const FieldGroup = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  margin: 0.5rem;
  
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(102, 126, 234, 0.3);
    transform: translateY(-2px);
  }
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.75rem;
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Input = styled.input`
  width: 100%;
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: #667eea;
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  min-height: 120px;
  resize: vertical;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
  
  &:focus {
    outline: none;
    border-color: #667eea;
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.1);
  color: white;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #667eea;
    background: rgba(255, 255, 255, 0.15);
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  option {
    background: #2a5298;
    color: white;
    padding: 0.5rem;
  }
`;

const Button = styled.button`
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s;
  }
  
  &:hover::before {
    left: 100%;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    background: rgba(255, 255, 255, 0.2);
    cursor: not-allowed;
    opacity: 0.6;
    transform: none;
  }
`;

const DeleteButton = styled(Button)`
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  
  &:hover {
    box-shadow: 0 8px 25px rgba(231, 76, 60, 0.4);
  }
`;

const SessionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-height: 600px;
  overflow-y: auto;
  padding-right: 0.5rem;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(102, 126, 234, 0.5);
    border-radius: 10px;
    
    &:hover {
      background: rgba(102, 126, 234, 0.7);
    }
  }
`;

const SessionCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  padding: 1.5rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #667eea, #764ba2);
    border-radius: 12px 12px 0 0;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  }
`;

const SessionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 1rem;
`;

const SessionTitle = styled.h3`
  color: white;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const SessionMeta = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const MetaItem = styled.div`
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
`;

const StatusBadge = styled.span`
  padding: 0.5rem 1rem;
  border-radius: 25px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  
  ${props => {
    switch (props.status) {
      case 'scheduled':
        return 'background: linear-gradient(135deg, #f39c12, #e67e22); color: white;';
      case 'live':
        return 'background: linear-gradient(135deg, #27ae60, #2ecc71); color: white; animation: pulse 2s infinite;';
      case 'ended':
        return 'background: linear-gradient(135deg, #95a5a6, #7f8c8d); color: white;';
      case 'cancelled':
        return 'background: linear-gradient(135deg, #e74c3c, #c0392b); color: white;';
      default:
        return 'background: linear-gradient(135deg, #95a5a6, #7f8c8d); color: white;';
    }
  }}
  
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.8; }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const FormSection = styled.div`
  margin-bottom: 2rem;
`;

const SectionHeading = styled.h3`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.2rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(102, 126, 234, 0.3);
`;

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Convert datetime from admin's selected timezone to UTC
const convertToUTC = (datetimeString, timezone) => {
  if (!datetimeString || !timezone) return null;
  
  // Create date in the admin's selected timezone
  const date = new Date(datetimeString);
  
  // Format as UTC ISO string
  return date.toISOString();
};

// Common timezone options for admin
const timezoneOptions = [
  { value: 'Asia/Kolkata', label: 'India Time (IST) - UTC+5:30' },
  { value: 'America/New_York', label: 'Eastern Time (ET) - UTC-5:00' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT) - UTC-8:00' },
  { value: 'Europe/London', label: 'London Time (GMT) - UTC+0:00' },
  { value: 'Europe/Paris', label: 'Central European Time (CET) - UTC+1:00' },
  { value: 'Asia/Tokyo', label: 'Japan Time (JST) - UTC+9:00' },
  { value: 'Asia/Shanghai', label: 'China Time (CST) - UTC+8:00' },
  { value: 'Australia/Sydney', label: 'Australia Time (AEST) - UTC+10:00' },
  { value: 'UTC', label: 'UTC - Universal Time' }
];

function LiveSessionManagement({ showMessage }) {
  const [sessions, setSessions] = useState([]);
  const [experts, setExperts] = useState([]);
  const [form, setForm] = useState({
    title: '',
    description: '',
    teacher_name: '',
    topic_title: '',
    session_duration: 60,
    difficulty_level: 'Intermediate',
    zoom_link: '',
    meeting_id: '',
    meeting_password: '',
    start_time: '',
    end_time: '',
    admin_timezone: 'Asia/Kolkata', // Default to Indian timezone
    max_attendees: 1000,
    current_attendees: 0,
    status: 'scheduled',
    is_active: true,
    expert_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const fetchSessions = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/live-sessions`);
      const data = await response.json();
      if (data.success) {
        setSessions(data.data);
      }
    } catch (error) {
      showMessage('Error fetching live sessions', 'error');
    }
  }, [showMessage]);

  const fetchExperts = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/experts`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      if (data.success) {
        setExperts(data.data);
      }
    } catch (error) {
      showMessage('Error fetching experts', 'error');
    }
  }, [showMessage]);

  useEffect(() => {
    fetchSessions();
    fetchExperts();
  }, [fetchSessions, fetchExperts]);

  // Auto-fill teacher name when expert is selected
  useEffect(() => {
    if (form.expert_id && experts.length > 0) {
      const selectedExpert = experts.find(expert => expert.id === form.expert_id);
      if (selectedExpert) {
        setForm(prev => ({
          ...prev,
          teacher_name: selectedExpert.full_name
        }));
      }
    } else if (!form.expert_id) {
      // Clear teacher name if no expert is selected
      setForm(prev => ({
        ...prev,
        teacher_name: ''
      }));
    }
  }, [form.expert_id, experts]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      // Convert admin's selected timezone to UTC before sending
      const formData = {
        ...form,
        start_time: form.start_time ? convertToUTC(form.start_time, form.admin_timezone) : null,
        end_time: form.end_time ? convertToUTC(form.end_time, form.admin_timezone) : null
      };
      
      const url = editingId 
        ? `${API_BASE_URL}/api/v1/live-sessions/${editingId}`
        : `${API_BASE_URL}/api/v1/live-sessions`;
      
      const method = editingId ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        showMessage(`Live session ${editingId ? 'updated' : 'created'} successfully`);
        setForm({
          title: '',
          description: '',
          teacher_name: '',
          topic_title: '',
          session_duration: 60,
          difficulty_level: 'Intermediate',
          zoom_link: '',
          meeting_id: '',
          meeting_password: '',
          start_time: '',
          end_time: '',
          admin_timezone: 'Asia/Kolkata', // Reset to default
          max_attendees: 1000,
          current_attendees: 0,
          status: 'scheduled',
          is_active: true,
          expert_id: ''
        });
        setEditingId(null);
        fetchSessions();
      } else {
        showMessage(data.message || `Error ${editingId ? 'updating' : 'creating'} live session`, 'error');
      }
    } catch (error) {
      showMessage('Network error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (session) => {
    setForm({
      ...session,
      start_time: new Date(session.start_time).toISOString().slice(0, 16),
      end_time: new Date(session.end_time).toISOString().slice(0, 16),
      admin_timezone: 'Asia/Kolkata', // Default to India when editing
      expert_id: session.expert_id || ''
    });
    setEditingId(session.id);
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('Are you sure you want to delete this live session?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/live-sessions/${sessionId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      const data = await response.json();
      
      if (data.success) {
        showMessage('Live session deleted successfully');
        fetchSessions();
      } else {
        showMessage(data.message || 'Error deleting live session', 'error');
      }
    } catch (error) {
      showMessage('Network error', 'error');
    }
  };

  const formatDateTime = (dateString) => {
    // For admin panel, show in browser's local timezone for clarity
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  return (
    <Container>
      <SectionTitle>Live Session Management</SectionTitle>
      
      <Form onSubmit={handleSubmit}>
        <FormSection>
          <SectionHeading>📝 Basic Information</SectionHeading>
          <FieldGroup>
            <Label>Session Title</Label>
            <Input
              type="text"
              placeholder="Enter an engaging session title"
              value={form.title}
              onChange={(e) => setForm({...form, title: e.target.value})}
              required
            />
          </FieldGroup>

          <FieldGroup>
            <Label>Description</Label>
            <TextArea
              placeholder="Describe what students will learn in this session..."
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
            />
          </FieldGroup>
        </FormSection>

        <FormSection>
          <SectionHeading>👨‍🏫 Instructor Details</SectionHeading>
          <FieldGroup>
            <Label>Select Expert</Label>
            <Select
              value={form.expert_id}
              onChange={(e) => setForm({...form, expert_id: e.target.value})}
            >
              <option value="">Choose an expert (optional)</option>
              {experts.map(expert => (
                <option key={expert.id} value={expert.id}>
                  {expert.full_name} - {expert.qualification || 'Expert'}
                </option>
              ))}
            </Select>
            <small style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
              Select an expert from the users table with role 'expert'
            </small>
            
            {/* Display selected expert details */}
            {form.expert_id && experts.length > 0 && (() => {
              const selectedExpert = experts.find(expert => expert.id === form.expert_id);
              if (!selectedExpert) return null;
              return (
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '1rem', 
                  background: 'rgba(102, 126, 234, 0.1)', 
                  borderRadius: '8px',
                  border: '1px solid rgba(102, 126, 234, 0.3)'
                }}>
                  <h4 style={{ color: 'white', margin: '0 0 0.5rem 0', fontSize: '0.9rem' }}>
                    📋 Expert Details
                  </h4>
                  <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: '0.25rem 0', fontSize: '0.85rem' }}>
                    <strong>Name:</strong> {selectedExpert.full_name}
                  </p>
                  {selectedExpert.qualification && (
                    <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: '0.25rem 0', fontSize: '0.85rem' }}>
                      <strong>Qualification:</strong> {selectedExpert.qualification}
                    </p>
                  )}
                  {selectedExpert.industry_experience && (
                    <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: '0.25rem 0', fontSize: '0.85rem' }}>
                      <strong>Experience:</strong> {selectedExpert.industry_experience} years
                    </p>
                  )}
                  {selectedExpert.bio && (
                    <p style={{ color: 'rgba(255, 255, 255, 0.9)', margin: '0.25rem 0', fontSize: '0.85rem' }}>
                      <strong>Bio:</strong> {selectedExpert.bio.substring(0, 100)}{selectedExpert.bio.length > 100 ? '...' : ''}
                    </p>
                  )}
                </div>
              );
            })()}
          </FieldGroup>

          <FieldGroup>
            <Label>Teacher Name</Label>
            <Input
              type="text"
              placeholder="Enter instructor's full name"
              value={form.teacher_name}
              onChange={(e) => setForm({...form, teacher_name: e.target.value})}
              readOnly={!!form.expert_id}
              style={{
                backgroundColor: form.expert_id ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.1)',
                cursor: form.expert_id ? 'not-allowed' : 'text'
              }}
              required
            />
            {form.expert_id && (
              <small style={{ color: 'rgba(102, 126, 234, 0.8)', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
                ✅ Auto-filled from selected expert
              </small>
            )}
          </FieldGroup>

          <FieldGroup>
            <Label>Topic Title</Label>
            <Input
              type="text"
              placeholder="Enter the main topic or subject"
              value={form.topic_title}
              onChange={(e) => setForm({...form, topic_title: e.target.value})}
              required
            />
          </FieldGroup>
        </FormSection>

        <FormSection>
          <SectionHeading>⏰ Session Settings</SectionHeading>
          <FieldGroup>
            <Label>Duration (minutes)</Label>
            <Input
              type="number"
              placeholder="Session duration"
              value={form.session_duration}
              onChange={(e) => setForm({...form, session_duration: parseInt(e.target.value)})}
              min="15"
              max="480"
              required
            />
          </FieldGroup>

          <FieldGroup>
            <Label>Difficulty Level</Label>
            <Select
              value={form.difficulty_level}
              onChange={(e) => setForm({...form, difficulty_level: e.target.value})}
              required
            >
              <option value="Beginner">🌱 Beginner</option>
              <option value="Intermediate">🚀 Intermediate</option>
              <option value="Advanced">💪 Advanced</option>
            </Select>
          </FieldGroup>

          <FieldGroup>
            <Label>Max Attendees</Label>
            <Input
              type="number"
              placeholder="Maximum number of attendees"
              value={form.max_attendees}
              onChange={(e) => setForm({...form, max_attendees: parseInt(e.target.value)})}
              min="1"
              required
            />
          </FieldGroup>

          <FieldGroup>
            <Label>Current Attendees</Label>
            <Input
              type="number"
              placeholder="Current number of attendees"
              value={form.current_attendees}
              onChange={(e) => setForm({...form, current_attendees: parseInt(e.target.value)})}
              min="0"
            />
          </FieldGroup>

          <FieldGroup>
            <Label>Status</Label>
            <Select
              value={form.status}
              onChange={(e) => setForm({...form, status: e.target.value})}
              required
            >
              <option value="scheduled">📅 Scheduled</option>
              <option value="live">🔴 Live Now</option>
              <option value="ended">✅ Ended</option>
              <option value="cancelled">❌ Cancelled</option>
            </Select>
          </FieldGroup>
        </FormSection>

        <FormSection>
          <SectionHeading>🎥 Zoom Integration</SectionHeading>
          <FieldGroup>
            <Label>Zoom Link</Label>
            <Input
              type="url"
              placeholder="https://zoom.us/j/..."
              value={form.zoom_link}
              onChange={(e) => setForm({...form, zoom_link: e.target.value})}
              required
            />
          </FieldGroup>

          <FieldGroup>
            <Label>Meeting ID</Label>
            <Input
              type="text"
              placeholder="Zoom meeting ID (optional)"
              value={form.meeting_id}
              onChange={(e) => setForm({...form, meeting_id: e.target.value})}
            />
          </FieldGroup>

          <FieldGroup>
            <Label>Meeting Password</Label>
            <Input
              type="text"
              placeholder="Meeting password (optional)"
              value={form.meeting_password}
              onChange={(e) => setForm({...form, meeting_password: e.target.value})}
            />
          </FieldGroup>
        </FormSection>

        <FormSection>
          <SectionHeading>📅 Schedule</SectionHeading>
          
          <FieldGroup>
            <Label>Timezone</Label>
            <Select
              value={form.admin_timezone}
              onChange={(e) => setForm({...form, admin_timezone: e.target.value})}
            >
              {timezoneOptions.map(tz => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </Select>
            <small style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.8rem', marginTop: '0.5rem', display: 'block' }}>
              Select the timezone for the session times you're entering
            </small>
          </FieldGroup>

          <FieldGroup>
            <Label>Start Time</Label>
            <Input
              type="datetime-local"
              value={form.start_time}
              onChange={(e) => setForm({...form, start_time: e.target.value})}
              required
            />
          </FieldGroup>

          <FieldGroup>
            <Label>End Time</Label>
            <Input
              type="datetime-local"
              value={form.end_time}
              onChange={(e) => setForm({...form, end_time: e.target.value})}
              required
            />
          </FieldGroup>
        </FormSection>

        <FullWidthField>
          <Button type="submit" disabled={loading}>
            {loading ? '💾 Saving...' : (editingId ? '✏️ Update Session' : '🚀 Create Session')}
          </Button>
          {editingId && (
            <Button 
              type="button" 
              onClick={() => {
                setEditingId(null);
                setForm({
                  title: '',
                  description: '',
                  teacher_name: '',
                  topic_title: '',
                  session_duration: 60,
                  difficulty_level: 'Intermediate',
                  zoom_link: '',
                  meeting_id: '',
                  meeting_password: '',
                  start_time: '',
                  end_time: '',
                  max_attendees: 1000,
                  current_attendees: 0,
                  status: 'scheduled',
                  is_active: true,
                  expert_id: ''
                });
              }}
              style={{ marginLeft: '1rem' }}
            >
              ❌ Cancel
            </Button>
          )}
        </FullWidthField>
      </Form>

      <SessionList>
        {sessions.map(session => (
          <SessionCard key={session.id}>
            <SessionHeader>
              <div>
                <SessionTitle>{session.title}</SessionTitle>
                <StatusBadge status={session.status}>
                  {session.status}
                </StatusBadge>
              </div>
            </SessionHeader>
            
            <SessionMeta>
              <MetaItem><strong>Teacher:</strong> {session.teacher_name}</MetaItem>
              {session.expert_id && (
                <MetaItem>
                  <strong>Expert ID:</strong> {session.expert_id}
                </MetaItem>
              )}
              <MetaItem><strong>Topic:</strong> {session.topic_title}</MetaItem>
              <MetaItem><strong>Duration:</strong> {session.session_duration} minutes</MetaItem>
              <MetaItem><strong>Level:</strong> {session.difficulty_level}</MetaItem>
              <MetaItem><strong>Start:</strong> {formatDateTime(session.start_time)}</MetaItem>
              <MetaItem><strong>End:</strong> {formatDateTime(session.end_time)}</MetaItem>
              <MetaItem><strong>Attendees:</strong> {session.current_attendees}/{session.max_attendees}</MetaItem>
              <MetaItem><strong>Zoom:</strong> {session.zoom_link}</MetaItem>
            </SessionMeta>
            
            {session.description && (
              <MetaItem style={{ gridColumn: '1 / -1' }}>
                <strong>Description:</strong> {session.description}
              </MetaItem>
            )}
            
            <ButtonGroup>
              <Button onClick={() => handleEdit(session)}>
                Edit
              </Button>
              <DeleteButton onClick={() => handleDelete(session.id)}>
                Delete
              </DeleteButton>
            </ButtonGroup>
          </SessionCard>
        ))}
      </SessionList>
    </Container>
  );
}

export default LiveSessionManagement;
